import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '@/utils/date_utils';
import { quoteStatuses, defaultQuoteStatuses } from '@/root/defaultConfiguration';
import { useWidgetData } from '../useWidgetData';
import { LABEL_COLOR } from '../constants';
import { WidgetCard } from './WidgetCard';

interface QuotationsWidgetProps {
    enabled: boolean;
}

const WIDGET_TITLE = 'Needed Attention Quotes';
const WIDGET_SUBTITLE = 'Quotes with pending follow-ups, oldest first';

function getQuoteStatusLabel(status: number): string {
    const statusObj = defaultQuoteStatuses.find((s) => parseInt(s.value) === status);
    return statusObj?.label ?? `Status ${status}`;
}

function getQuoteStatusColor(status: number): string {
    const statusObj = defaultQuoteStatuses.find((s) => parseInt(s.value) === status);
    return statusObj?.color ?? '#7dbde8';
}

export const QuotationsWidget = ({ enabled }: QuotationsWidgetProps) => {
    const navigate = useNavigate();
    const { data, isLoading, error } = useWidgetData('quotations', enabled);

    if (error) {
        return (
            <WidgetCard
                title={WIDGET_TITLE}
                subtitle={WIDGET_SUBTITLE}
                children={
                    <Typography variant="body2" sx={{ color: '#EF4444' }}>
                        Failed to load quotations widget
                    </Typography>
                }
            />
        );
    }

    if (isLoading || !data) {
        return (
            <WidgetCard
                title={WIDGET_TITLE}
                subtitle={WIDGET_SUBTITLE}
                loading
            />
        );
    }

    const hasRows = data.rows && data.rows.length > 0;

    return (
        <WidgetCard
            title={WIDGET_TITLE}
            subtitle={WIDGET_SUBTITLE}
            headline={
                <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                    {data.total_count} total
                </Typography>
            }
        >
            {!hasRows ? (
                <Typography variant="body2" sx={{ color: LABEL_COLOR }}>
                    No quotes need attention
                </Typography>
            ) : (
                <Stack spacing={1.5}>
                    {data.rows.map((row) => (
                        <Box
                            key={row.id}
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: '#F9FAFB',
                                p: 1.5,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                    backgroundColor: '#F3F4F6',
                                },
                            }}
                            onClick={() => navigate(`/quotes/${row.id}/show`)}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={1}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, color: '#111827', mb: 0.25 }}
                                    >
                                        {row.description || `Quote #${row.id}`}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: LABEL_COLOR, display: 'block', mb: 0.5 }}>
                                        {row.address || 'No address'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                                        {formatDate(row.created_at)}
                                        {data.scope === 'team' && row.created_by_name
                                            ? ` · ${row.created_by_name}`
                                            : ''}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={getQuoteStatusLabel(row.status)}
                                    size="small"
                                    sx={{
                                        backgroundColor: getQuoteStatusColor(row.status),
                                        color: '#fff',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                    }}
                                />
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}
        </WidgetCard>
    );
};

QuotationsWidget.displayName = 'QuotationsWidget';
